/**
 * PortfolioCard - Mobile
 * 
 * Portfolio list item showing name, opportunity count, and value.
 * Structured, ledger-like presentation with smooth press animation.
 */

import React, { useRef } from 'react';
import { View, Text, Pressable, StyleSheet, Animated } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { color, text, space, touch, icon } from '../../../shared/tokens/mobile';

interface PortfolioCardProps {
  name: string;
  opportunityCount: number;
  totalValue?: number | null;
  onPress: () => void;
  hideBorder?: boolean;
}

export function PortfolioCard({
  name,
  opportunityCount,
  totalValue,
  onPress,
  hideBorder = false,
}: PortfolioCardProps) {
  const scale = useRef(new Animated.Value(1)).current;
  const valueDisplay = formatValue(totalValue);
  const countText = opportunityCount === 1 ? '1 opportunity' : `${opportunityCount} opportunities`;

  const handlePressIn = () => {
    Animated.spring(scale, { toValue: 0.98, useNativeDriver: true, speed: 50 }).start();
  };

  const handlePressOut = () => {
    Animated.spring(scale, { toValue: 1, useNativeDriver: true, speed: 50 }).start();
  };

  return (
    <Pressable
      onPress={onPress}
      onPressIn={handlePressIn}
      onPressOut={handlePressOut}
      accessibilityRole="button"
      accessibilityLabel={`${name}, ${countText}`}
    >
      <Animated.View style={[styles.card, !hideBorder && styles.border, { transform: [{ scale }] }]}>
        <Ionicons name="folder-outline" size={icon.md} color={color.textSecondary} />
        <View style={styles.content}>
          <Text style={styles.name} numberOfLines={1}>{name}</Text>
          <Text style={styles.meta} numberOfLines={1}>
            {countText}{valueDisplay ? ` • ${valueDisplay}` : ''}
          </Text>
        </View>
        <Ionicons name="chevron-forward" size={icon.sm + 2} color={color.textMuted} />
      </Animated.View>
    </Pressable>
  );
}

function formatValue(value: number | null | undefined): string | null {
  if (!value) return null;
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `$${Math.round(value / 1000)}K`;
  return `$${value.toLocaleString()}`;
}

const styles = StyleSheet.create({
  card: {
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: touch.listRow,
    paddingVertical: space.sm + space.xs,
    paddingHorizontal: space.md,
    gap: space.sm + space.xs,
    backgroundColor: color.surface,
  },
  border: {
    borderBottomWidth: 1,
    borderBottomColor: color.divider,
  },
  content: {
    flex: 1,
  },
  name: {
    fontSize: text.body.fontSize,
    fontWeight: text.bodyMedium.fontWeight,
    lineHeight: text.body.lineHeight,
    color: color.textPrimary,
  },
  meta: {
    fontSize: text.caption.fontSize,
    fontWeight: text.caption.fontWeight,
    lineHeight: text.caption.lineHeight,
    color: color.textSecondary,
    marginTop: space.xs / 2,
  },
});
